import type { Experiment } from "@andrum/schemas";

export const MOCK_EXPERIMENTS: Experiment[] = [
  {
    id: "11111111-1111-4111-8111-111111110101",
    slug: "call-someone-you-miss",
    category: "connection",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Hör av dig till någon du saknar",
    summary: "Ett kort meddelande eller samtal — konkret kontakt slår “jag borde höra av mig”.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Välj en person. Öppna meddelanden eller ringlistan — ingen perfekt text krävs.", "Skicka något enkelt: “Tänker på dig” / en emoji / “Hur är det?”. Eller ring 1–2 minuter.", "Lägg märke till hur det kändes att skicka/ringa — inte till om de svarar direkt."],
    smallerVersion: "En emoji eller en rad till någon.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110102",
    slug: "walk-without-phone",
    category: "presence",
    difficulty: 2,
    durationMinutes: 5,
    minDurationMinutes: 2,
    premium: false,
    title: "Promenad utan skärm",
    summary: "Några minuter i rörelse utan input — uppmärksamhetsvila + lätt aktivering.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Lämna telefonen eller sätt den i fickan på tyst. Gå ut eller i korridor.", "Gå 3–5 minuter. Välj ett “ankare”: fotsteg, andning, eller tre saker du ser.", "Innan du tar upp skärmen: namnge en sak som kändes annorlunda i kroppen."],
    smallerVersion: "90 sekunder vid ett fönster utan skärm, tre andetag.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110103",
    slug: "three-gratitudes",
    category: "gratitude",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Tre konkreta tack",
    summary: "Nämn tre saker från i dag — små och specifika, inte vaga “allt är bra”.",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet.",
    instructions: ["Stanna 30 sekunder. Tänk på i dag, inte livet i stort.", "Säg eller skriv tre konkreta saker (en person, en sak, en stund). Ju mer specifik, desto bättre.", "Välj en av dem och stanna 20 sekunder hos känslan — var i kroppen märks den?"],
    smallerVersion: "En konkret sak från i dag, 20 sekunder.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110104",
    slug: "two-minute-breath",
    category: "recovery",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Två minuters andning (längre ut)",
    summary: "Andas med längre utandning i ungefär två minuter — en snabb signal att sänka påslag.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Sätt en timer på 2 minuter. Sitt eller stå. Axlarna mjuka.", "Andas in genom näsan ca 4, ut genom munnen ca 6. Om du tappar räkningen: starta om utan självkritik.", "När timern ringer: notera en sak — mer stilla, samma, eller mer orolig. Inget rätt svar."],
    smallerVersion: "Sex andetag med längre utandning.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110105",
    slug: "stretch-shoulders",
    category: "movement",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Mjuka axlar och nacke",
    summary: "Tre minuter stilla rörelse vid skrivbordet eller soffan.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Rulla axlarna bakåt fem gånger, långsamt.", "Luta huvudet mjukt mot ena axeln, andas, byt sida.", "Sträck armarna uppåt och andas ut när du sänker dem. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Rulla axlarna bakåt tre gånger där du sitter.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110106",
    slug: "one-kind-sentence",
    category: "meaning",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "En mening till dig själv",
    summary: "Skriv en vänlig mening du skulle säga till en god vän.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Tänk hur du skulle trösta en vän som har det tufft.", "Skriv en mening med samma värme, till dig själv.", "Läs den en gång tyst. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Säg tyst: “Jag gör det jag kan just nu.”",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110107",
    slug: "water-and-window",
    category: "recovery",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Vatten och utsikt",
    summary: "Drick ett glas vatten och titta ut en stund.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Förbered: Häll upp eller ta fram ett glas vatten.", "Drick sakta medan du tittar ut (eller på en växt).", "Lägg märke till en färg eller form framför dig."],
    smallerVersion: "Ta tre klunkar och titta bort från skärmen.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110108",
    slug: "thank-someone",
    category: "connection",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Tacka någon konkret",
    summary: "Skicka ett kort tack för något specifikt någon gjort.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Välj någon som nyligen gjort något litet snällt.", "Skriv vad det var och hur det kändes.", "Skicka utan att vänta på svar. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Skriv “Tack för igår” och skicka.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110111",
    slug: "five-senses-reset",
    category: "presence",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Fem sinnen — snabb reset",
    summary: "Förankra dig i nuet med 5-4-3-2-1: det du ser, känner, hör, doftar, smakar.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Titta: namnge 5 saker du ser (färger, former), tyst eller högt.", "Kropp & ljud: 4 saker du känner mot huden, 3 ljud du hör just nu.", "Avsluta: 2 dofter (eller minnen av doft) och 1 smak. Andas ut en gång långsamt."],
    smallerVersion: "3 saker du ser + 2 ljud + 1 utandning.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110112",
    slug: "box-breathing",
    category: "recovery",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Boxandning 4-4-4-4",
    summary: "Fyra lika delar: in, håll, ut, håll. En tydlig struktur när tankarna snurrar.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Sitt stadigt. Välj tyst räkning till 4 (eller 3 om 4 känns för långt).", "Gör 4–6 cykler: in 4 · håll 4 · ut 4 · håll 4. Håll käken mjuk.", "Avsluta med ett vanligt andetag. Notera om tempot i kroppen ändrades."],
    smallerVersion: "Två cykler med räkning till 3.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110113",
    slug: "feet-on-floor",
    category: "presence",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Fötter i golvet",
    summary: "Snabb grounding: kontakt med underlaget när det snurrar.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Ställ båda fötterna i golvet. Känn sulorna, hälarna, tårna.", "Tryck mjukt ner i 10 sekunder, släpp. Upprepa tre gånger.", "Namnge tre saker du ser. Andas ut en gång långsamt."],
    smallerVersion: "Tryck fötterna i golvet i 10 sekunder + en utandning.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110114",
    slug: "jaw-and-shoulders-release",
    category: "movement",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Släpp käke och axlar",
    summary: "Släpp mjukt de ställen som håller vardagsspänning.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Låt tungan vila och släpp ihopbitna tänder.", "Sänk axlarna från öronen.", "Ta tre långsamma andetag i den mjukare hållningen. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Sänk bara axlarna en gång och sucka.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110115",
    slug: "cold-water-wrists",
    category: "recovery",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Svalt vatten på handlederna",
    summary: "Kör svalt vatten över handlederna eller skölj ansiktet lätt.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Förbered: Använd svalt (inte isande) vatten.", "Håll handlederna under vatten 20–30 sekunder eller skölj ansiktet mjukt.", "Torka av och lägg märke till temperatur och andning."],
    smallerVersion: "Håll ett svalt glas mot handlederna i 20 sekunder.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110116",
    slug: "one-song-move",
    category: "movement",
    difficulty: 2,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "En låt i rörelse",
    summary: "Rör dig till en låt — mjuk aktivering utan träningskrav.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Välj en låt (2–4 min). Ställ dig upp om du kan.", "Rör dig hur du vill: gunga, gå på stället, stretcha. Inget rätt sätt.", "När låten tar slut: tre andetag. Notera energi 0–10 ungefär."],
    smallerVersion: "30 sekunder gungning till en refräng.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110117",
    slug: "step-outside-two-minutes",
    category: "presence",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Gå ut i två minuter",
    summary: "Gå ut eller till en balkong; känn luft och ljus.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Förbered: Steg ut eller öppna en dörr om du kan.", "Känn luft mot huden och notera ljuset.", "Stanna ungefär två minuter utan multitasking. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Öppna ett fönster och andas utomhusluft tre gånger.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110118",
    slug: "tidy-one-surface",
    category: "meaning",
    difficulty: 1,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "Rensa en yta",
    summary: "Rensa en liten yta helt.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Välj en yta (skrivbordshörn, sängbord, handfatskant).", "Ta bort eller ställ undan det som inte hör hemma.", "Torka av en gång om du vill; sluta när det är rent. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Ställ bara undan tre saker.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110119",
    slug: "voice-note-to-friend",
    category: "connection",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Röstmeddelande till en vän",
    summary: "Skicka ett varmt röstmeddelande på 20–40 sekunder.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Förbered: Välj någon trygg.", "Spela in 20–40 sekunder: hej och en ärlig mening.", "Skicka utan att polera. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Skicka ett 5-sekunders “tänker på dig”.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110120",
    slug: "worry-parking",
    category: "recovery",
    difficulty: 2,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "Parkera oron",
    summary: "Skriv ner oron och boka en senare “orostid” — klassisk CBT-idé mot ältande.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Skriv oro i 60–90 sekunder: punktlista räcker. Ingen lösning krävs nu.", "Skriv en tid i dag eller i morgon för 10 minuters orostid (t.ex. 17:30).", "När oron dyker upp före dess: säg tyst Den är parkerad till [tid] och återvänd till det du gjorde."],
    smallerVersion: "Skriv tre orospunkter och en tid för senare.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110121",
    slug: "mindful-sip",
    category: "presence",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Medvetna klunkar",
    summary: "Drick de första klunkarna med full uppmärksamhet.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Förbered: Håll en kopp eller ett glas.", "Lägg märke till temperatur, doft och de tre första klunkarna.", "Fortsätt sedan som vanligt. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Ta en helt uppmärksam klunk.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110122",
    slug: "hand-on-heart",
    category: "meaning",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Hand på hjärtat",
    summary: "Mjuk beröring + andning — kroppens signal att det får vara tryggt en stund.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Lägg en hand på bröstet eller magen. Känn värme och tryck.", "Andas långsamt i ungefär en minut. Låt handen vara stilla.", "En tyst fras om du vill: Det är okej att det är som det är just nu."],
    smallerVersion: "Tio andetag med handen på bröstet.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110123",
    slug: "three-strengths-today",
    category: "gratitude",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Tre styrkor du använde idag",
    summary: "Namnge tre styrkor du redan använt, även små.",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet.",
    instructions: ["Förbered: Tänk på dagen hittills.", "Namnge tre styrkor du använt (tålamod, humor, fokus…).", "Notera var du kan använda en igen."],
    smallerVersion: "Namnge en styrka du använde senaste timmen.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110124",
    slug: "phone-in-another-room",
    category: "presence",
    difficulty: 2,
    durationMinutes: 5,
    minDurationMinutes: 2,
    premium: false,
    title: "Telefonen i ett annat rum",
    summary: "Miljö före vilja: gör skärmen lite svårare att nå i några minuter.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Lägg telefonen i ett annat rum (eller i en låda) i 5 minuter. Sätt en timer.", "Gör något enkelt under tiden: diska, sitta, andas, titta ut.", "När timern ringer: notera suget efter skärmen 0–10 innan du tar upp den."],
    smallerVersion: "Telefonen upp och ner, 2 minuter utan att kolla.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110125",
    slug: "one-bite-mindful",
    category: "presence",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "En medveten tugga",
    summary: "Ät en tugga som om du smakar den för första gången.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Förbered: Ta en tugga av något du har.", "Lägg märke till textur, smak och eftersmak.", "Svälj och pausa ett andetag. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Tugga en tugga dubbelt så länge som vanligt.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110126",
    slug: "calf-hip-unlock",
    category: "movement",
    difficulty: 1,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "Vad och höft-lås upp",
    summary: "Mjuk stretch för baksida ben och höfter efter sittande.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Stå och stretcha ena vaden mjukt, sedan den andra.", "Öppna ena höften med mjuk utfallsposition eller sittande figure-four.", "Andas långsamt på varje sida. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Rulla fötter och knip säte två gånger per sida.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110127",
    slug: "window-light-break",
    category: "recovery",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Ljus- och avståndspaus",
    summary: "Titta långt bort mot dagsljus för en kort visuell vila.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Vänd dig mot fönster eller utomhusljus om du kan.", "Fokusera på något långt bort i 20–30 sekunder.", "Blinka långsamt och sänk axlarna. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Titta på rummets mest avlägsna punkt i 15 sekunder.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110128",
    slug: "one-sentence-journal",
    category: "meaning",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Enmeningsdagbok",
    summary: "Skriv en ärlig mening om just nu.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Förbered: Öppna en anteckning.", "Skriv en sann mening om hur du har det.", "Stäng den; ingen redigering krävs. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Fyll i: “Just nu …”",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110129",
    slug: "tiny-favor",
    category: "connection",
    difficulty: 2,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "En liten tjänst",
    summary: "Gör en liten hjälpsam handling utan poängräkning.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Välj en liten tjänst (tips, håll upp dörr, gör te, rensa delad yta).", "Gör den utan hjälteutspel.", "Lägg märke till hur det känns efteråt."],
    smallerVersion: "Skicka en nyttig länk eller en snäll reaktion.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110130",
    slug: "posture-soften",
    category: "movement",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Mjukare hållning",
    summary: "Skanna hållningen och släpp onödig ansträngning.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Lägg märke till käke, axlar, mage och händer.", "Släpp 10% av ansträngningen i varje.", "Ta två andetag i den mjukare formen. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Släpp händerna och sänk axlarna en gång.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110131",
    slug: "name-three-sounds",
    category: "presence",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Namnge tre ljud",
    summary: "Blunda gärna och etiketter tre ljud.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Förbered: Pausa och lyssna.", "Namnge tyst tre skilda ljud.", "Öppna ögonen och återgå till det du gjorde. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Namnge bara ett ljud.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110132",
    slug: "plan-one-small-joy",
    category: "gratitude",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Planera en liten glädje",
    summary: "Boka en liten trevlig sak inom 24 timmar.",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet.",
    instructions: ["Förbered: Välj något litet och realistiskt.", "Lägg det i kalendern eller en lapp med tid.", "Skydda det som en riktig bokning. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Bestäm en glädje du kan göra inom en timme.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110133",
    slug: "look-at-good-memory",
    category: "gratitude",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Titta på ett fint minne",
    summary: "Titta på ett foto eller minne som fortfarande värmer.",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet.",
    instructions: ["Förbered: Välj ett foto eller minne.", "Titta eller återkalla i en minut med detalj (plats, personer, känsla).", "Tacka det ögonblicket tyst. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Återkalla en bra 10-sekunders scen från den här månaden.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110134",
    slug: "hum-or-sigh",
    category: "recovery",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Lång suck eller mjukt hummande",
    summary: "Använd en lång utandning eller mjukt hummande för att växla ner.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Förbered: Andas in mjukt.", "Andas ut med en lång suck, eller humma på utandningen.", "Upprepa tre gånger. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "En lång suck med sänkta axlar.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110135",
    slug: "compliment-specific",
    category: "connection",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Ge en konkret komplimang",
    summary: "Säg till någon en specifik sak du uppskattar.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Förbered: Välj person och en konkret detalj.", "Säg eller skicka det tydligt.", "Lägg inte till en begäran efteråt. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Beröm en insats du sett idag.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110136",
    slug: "two-minute-desk-reset",
    category: "movement",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Tvåminuters skrivbordsreset",
    summary: "Stå upp, öppna bröstet och rör ryggraden mjukt.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Förbered: Stå upp om du kan.", "Sträck armarna ut åt sidorna, sedan mjuka sidoböjningar.", "Skaka loss händerna och sitt eller fortsätt stå. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Stå och rulla axlarna i 30 sekunder.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110301",
    slug: "values-one-word",
    category: "meaning",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Ett värdeord",
    summary: "Välj ett värde för nästa stund och en handling som matchar — ACT-lite.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Lista snabbt några värden (omsorg, nyfikenhet, vila, mod, närvaro …).", "Välj ett ord för de kommande 60 minuterna.", "Gör en minsta handling som passar ordet (en mening, en stretch, en gräns)."],
    smallerVersion: "Välj ett ord och skriv det på en lapp.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110302",
    slug: "stretch-and-breathe-walk",
    category: "movement",
    difficulty: 2,
    durationMinutes: 5,
    minDurationMinutes: 2,
    premium: false,
    title: "Promenad med stretch och andning",
    summary: "Kombinera lätt gång med längre utandningar.",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen.",
    instructions: ["Förbered: Gå långsamt inomhus eller ute.", "Matcha steg mot andning; gör utandningen lite längre.", "Fortsätt ungefär fem minuter. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Gå till ett annat rum med tre långa utandningar.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110303",
    slug: "send-good-news",
    category: "gratitude",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Dela en liten god nyhet",
    summary: "Skicka någon en liten god eller “good enough”-nyhet.",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet.",
    instructions: ["Förbered: Välj en liten eller medelstor nyhet.", "Skicka till någon som tar emot den väl.", "Ta emot svaret utan att förminska dig. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "Dela en bra rad från din dag.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110304",
    slug: "sit-in-silence",
    category: "presence",
    difficulty: 2,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Sitt i tystnad",
    summary: "Sitt utan input ungefär två minuter.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Förbered: Sätt en tvåminuterstimer.", "Sitt utan telefon, musik eller uppgifter.", "Lägg märke till suget efter input; återvänd till andning eller rums ljud."],
    smallerVersion: "Trettio sekunders tystnad med mjuka ögon.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110201",
    slug: "evening-wind-down",
    category: "recovery",
    difficulty: 3,
    durationMinutes: 14,
    minDurationMinutes: 6,
    premium: true,
    title: "Kvällens mjuka landning",
    summary: "En Plus-ritual i fem faser: ljus, skärm, andning, dump och en vänlig avslutning mot sömn.",
    rationale: "Längre kvällsritualer ger hjärnan tydliga signaler att dagen tar slut — mer än en enstaka andetag.",
    instructions: ["Fas 1 · Ljus (2 min): dämpa lampor eller byt till mjukare belysning. Lägg telefonen i ett annat rum eller på flygplansläge om du kan.", "Fas 2 · Kropp (2 min): sänk axlarna, rulla nacken mjukt, tre långa utandningar genom munnen.", "Fas 3 · Andning (4 min): andas in 4, ut 6. Om du tappar räkningen — starta om utan självkritik.", "Fas 4 · Dump (3 min): skriv tre rader: öppna loopar, oro, morgondagens minsta steg. Stäng blocket.", "Fas 5 · Släpp (2 min): en mening till dig själv, t.ex. Jag har gjort nog i dag. Hand på bröstet. Klart."],
    smallerVersion: "Sex minuter: dämpa ljus, andning 4–6 i tre minuter, en dump-rad och en vänlig mening.",
    reflectionQuestions: ["Vilken fas kändes mest som landning?", "Blev det lättare att släppa skärmen?", "Vad vill du upprepa i morgon kväll?", "Vill du ha ritualen i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110202",
    slug: "deep-connection-letter",
    category: "connection",
    difficulty: 3,
    durationMinutes: 14,
    minDurationMinutes: 7,
    premium: true,
    title: "Brev till någon viktig",
    summary: "Expressivt skrivande om en relation — skicka eller behåll.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Fas 1 · Person (2 min): välj någon som betytt något. Ingen måste få läsa.", "Fas 2 · Fakta (3 min): när träffades ni / vad har ni delat? Korta minnen.", "Fas 3 · Betydelse (5 min): vad de betytt för dig. Specifikt, inte bara “du är bäst”.", "Fas 4 · Önskan (2 min): en hoppfull eller tacksam avslutning.", "Fas 5 · Beslut (2 min): skicka, spara, eller riva. Alla tre är okej."],
    smallerVersion: "Tre meningar du menar på allvar.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110203",
    slug: "body-scan-ten",
    category: "presence",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 5,
    premium: true,
    title: "Guidad kroppsscan",
    summary: "En längre Plus-scan i tydliga zoner: fötter, ben, bål, armar, huvud — utan att “fixa” något.",
    rationale: "Body scan är en kärnövning i mindfulness för interoception och nedvarvning. Plus-versionen är längre och mer guidande än free-stegen.",
    instructions: ["Fas 1 · Start (1–2 min): ligg eller sitt. Andas normalt. Bestäm att du inte ska prestera — bara märka.", "Fas 2 · Fötter & ben (3 min): uppmärksamhet i tår, fotsulor, vader, lår. Känn kontakt mot underlaget. Ingen bedömning.", "Fas 3 · Bål (3 min): mage, bröst, rygg. Notera andningens rörelse. Vid spänning: stanna, andas ut, gå vidare.", "Fas 4 · Armar & axlar (2 min): händer, armar, axlar. Låt axlarna sjunka en millimeter mer vid utandning.", "Fas 5 · Huvud & avslut (2 min): nacke, ansikte, panna. Avsluta med tre andetag och en tyst: Jag är här."],
    smallerVersion: "Fem minuter: fötter, mage, axlar, ansikte — sedan tre andetag.",
    reflectionQuestions: ["Vilken zon var tydligast?", "Mjukare, mer vaken, eller ungefär samma?", "Vad hände när tankarna vandrade?", "Vill du ha den här i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110204",
    slug: "values-compass-session",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Värdekompass-session",
    summary: "Kartlägg toppvärden och en veckohandling för tre av dem (Plus: längre, fasad guide).",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Lista 5–7 värden som spelar roll nu. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Ringa in tre.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Skriv en liten veckohandling per värde.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Välj ett värde och en handling bara.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110205",
    slug: "gratitude-letter-full",
    category: "gratitude",
    difficulty: 3,
    durationMinutes: 15,
    minDurationMinutes: 7,
    premium: true,
    title: "Hela tacksamhetsbrevet",
    summary: "Skriv ett längre tackbrev du kan läsa högt eller skicka (Plus: längre, fasad guide).",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Välj någon du inte fullt tackat. Andas ut en extra gång.", "Fas 2 · Kärna A (3 min): Skriv ungefär en sida med konkreta detaljer.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (3 min): Om det är tryggt: läs eller skicka; annars behåll.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Skriv ett kort tackstycke.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110206",
    slug: "nature-sit-fifteen",
    category: "presence",
    difficulty: 2,
    durationMinutes: 15,
    minDurationMinutes: 6,
    premium: true,
    title: "Sitta i mjuk naturuppmärksamhet",
    summary: "Attention restoration: 15 minuter mjuk fascination — löv, ljus, ljud — utan prestation.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter. Soft fascination (uppmärksamhetsvila) skiljer sig från att “tömma tankarna”.",
    instructions: ["Fas 1 · Plats (2 min): utomhus eller vid fönster med grönt/himmel. Telefon tyst och undan.", "Fas 2 · Se (4 min): följ ljus, färg, rörelse. Ingen analys — bara titta.", "Fas 3 · Hör (4 min): lager av ljud (närmast → längst bort).", "Fas 4 · Känn (3 min): luft mot huden, underlag, temperatur.", "Fas 5 · Avslut (2 min): en sak du tar med dig. Tre andetag. Klart."],
    smallerVersion: "Sex minuter vid fönster: 2 se, 2 hör, 2 andetag.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110207",
    slug: "loving-kindness-brief",
    category: "connection",
    difficulty: 2,
    durationMinutes: 12,
    minDurationMinutes: 5,
    premium: true,
    title: "Kärleksfull vänlighet (kort)",
    summary: "Korta fraser av välvilja till dig och andra — loving-kindness / metta-inspirerat.",
    rationale: "Loving-kindness-övningar kan öka värme och minska fientlighet i studier. Korta guidade faser är lättare att fullfölja än “meditera fritt”.",
    instructions: ["Fas 1 · Dig själv (3 min): upprepa tyst Må jag ha det okej. Må jag vara snäll mot mig. Må jag få vila.", "Fas 2 · Någon nära (3 min): samma fraser riktade till en person du tycker om.", "Fas 3 · Neutral person (2 min): någon du knappt känner (kassör, granne). Samma toner.", "Fas 4 · Svårare relation (2 min): bara om det känns tryggt — annars hoppa över. Mjuka fraser, inget tvingande förlåtande.", "Fas 5 · Alla (2 min): Må vi alla ha det lite lättare i dag. Tre andetag."],
    smallerVersion: "Bara fas 1: dig själv, 2 minuter.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110208",
    slug: "digital-sunset",
    category: "recovery",
    difficulty: 3,
    durationMinutes: 18,
    minDurationMinutes: 8,
    premium: true,
    title: "Digital solnedgång",
    summary: "En Plus-ritual som stänger skärmdagen i faser — miljö + vana, inte bara viljestyrka.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa. Skärmgränser funkar bättre som ritual än som enstaka förbud.",
    instructions: ["Fas 1 · Bestäm (2 min): välj en tid i kväll när skärmar går i “sömnläge” (t.ex. 21:30). Skriv den.", "Fas 2 · Förbered (3 min): laddkabel i annat rum, bok/te framme, notiser av.", "Fas 3 · Sista loop (5 min): svara bara det nödvändiga. Skriv tre rader “i morgon” om något skaver.", "Fas 4 · Avstängning (5 min): telefon/dator undan. Gör en icke-skärmsak: diska, stretcha, prata.", "Fas 5 · Skydda (3 min): om suget kommer — andas 5 utandningar innan du bryter. Notera sug 0–10."],
    smallerVersion: "Telefon i annat rum 30 min före säng + en analog sak.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110209",
    slug: "weekly-review-soft",
    category: "meaning",
    difficulty: 2,
    durationMinutes: 15,
    minDurationMinutes: 7,
    premium: true,
    title: "Mjuk veckorevy",
    summary: "En snäll genomgång: vad funkade, vad var tungt, ett litet nästa steg — utan poängjakt.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Fas 1 · Ramar (2 min): kaffe/te valfritt. Telefon undan. Rubrik: Den här veckan.", "Fas 2 · Tre bra (4 min): tre saker som faktiskt hände (små räknas).", "Fas 3 · Tre tunga (4 min): utan att lösa allt. Bara namnge.", "Fas 4 · Mönster (3 min): en mening: Jag mår oftare bättre när …", "Fas 5 · Nästa vecka (2 min): ett litet experiment eller gräns. Inte fem mål."],
    smallerVersion: "Tre rader: bra · tungt · ett nästa steg.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110210",
    slug: "progressive-muscle-release",
    category: "movement",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Progressiv muskelavspänning",
    summary: "Spänn och släpp muskelgrupper i ordning — klassisk PMR, ca 12 minuter.",
    rationale: "Progressiv muskelavspänning (Jacobson) är en klassisk metod för att lägga märke till och släppa kroppsspänning. Plus-versionen är sekventiell — free-steg räcker inte för hela kedjan.",
    instructions: ["Fas 1 · Start (1 min): sitt eller ligg. Tre andetag. Du ska spänna mjukt 5 sek, sedan släppa 10 sek — aldrig till smärta.", "Fas 2 · Ben (3 min): fötter → vader → lår. Spänn, släpp, känn skillnaden.", "Fas 3 · Bål (3 min): mage, skinkor, rygg (mjukt). Samma mönster.", "Fas 4 · Armar & axlar (3 min): händer, armar, axlar upp mot öronen, släpp.", "Fas 5 · Ansikte & avslut (2 min): käke, ögon, panna. Avsluta med tre långa utandningar."],
    smallerVersion: "Bara händer, axlar och käke: spänn 5 / släpp 10, två varv.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110211",
    slug: "relationship-check-in",
    category: "connection",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Relationscheck-in",
    summary: "Ett strukturerat, vänligt samtal med någon nära (Plus: längre, fasad guide).",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Be partner eller vän om 10 tysta minuter. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Var och en svarar: känsla, behov, en uppskattning.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Avsluta med en praktisk stödfråga om det behövs.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Skicka en text-check-in med de tre promptarna.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110212",
    slug: "strengths-in-action-plan",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Styrkor i handling",
    summary: "Välj två styrkor och planera verklig användning den här veckan (Plus: längre, fasad guide).",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Namnge dina toppstyrkor. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Välj två.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Planera var, när och hur du använder varje en gång den här veckan.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Använd en styrka en gång i morgon.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110213",
    slug: "savor-a-meal",
    category: "gratitude",
    difficulty: 2,
    durationMinutes: 15,
    minDurationMinutes: 7,
    premium: true,
    title: "Smaka en måltid",
    summary: "Ät en del av en måltid med full uppmärksamhet — förlänger det som annars försvinner.",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet. Att medvetet stanna kvar i något gott förlänger positiva ögonblick; det är mer än “ät långsammare”.",
    instructions: ["Fas 1 · Förbered (2 min): en portion eller en frukt. Skärm undan. Tre andetag.", "Fas 2 · Se & dofta (2 min): färg, ånga, doft — innan första tugga.", "Fas 3 · Första tuggorna (5 min): tugga långsamt. Lägg ner bestick mellan tuggor om du kan.", "Fas 4 · Mättnad (4 min): fortsätt men lägg märke till när “tillräckligt” dyker upp.", "Fas 5 · Eftersmak (2 min): en sak du faktiskt gillade. Tacka dig själv för pausen."],
    smallerVersion: "Tre tuggor i total tystnad utan skärm.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110214",
    slug: "brisk-mood-walk",
    category: "movement",
    difficulty: 2,
    durationMinutes: 15,
    minDurationMinutes: 7,
    premium: true,
    title: "Rask humörpromenad",
    summary: "En medveten 15-minuters promenad i lite högre tempo (Plus: längre, fasad guide).",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Gå 15 minuter i tempo som lätt höjer andningen. Andas ut en extra gång.", "Fas 2 · Kärna A (3 min): Lämna lurarna av minst halva tiden om du kan.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (3 min): Notera humör före och efter.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Åtta minuter runt kvarteret.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110215",
    slug: "self-compassion-break",
    category: "meaning",
    difficulty: 2,
    durationMinutes: 12,
    minDurationMinutes: 5,
    premium: true,
    title: "Självmedkänsla-paus (Plus)",
    summary: "Fördjupad self-compassion break med mer tid i varje del — Neff/MSC-struktur.",
    rationale: "Självmedkänsla-interventioner har i metaanalyser kopplats till minskad självkritik och stress. Plus ger tid per komponent; free-versionen är den korta pausen.",
    instructions: ["Fas 1 · Mindfulness (3 min): känn var det tar emot i kroppen. Säg: Det här är ett ögonblick av lidande/svårighet.", "Fas 2 · Common humanity (3 min): Säg: Svårigheter hör till livet. Andra känner så här också. Tänk på en “vi”-känsla.", "Fas 3 · Self-kindness (3 min): hand på bröstet. Välj fraser: Må jag ge mig den omtanke jag behöver. Må jag hålla mig sällskap.", "Fas 4 · Andning (2 min): andas in omtanke, ut spänning — i din egen takt.", "Fas 5 · Avslut (1 min): en minsta handling i linje med omtanke (vatten, paus, gräns)."],
    smallerVersion: "Free-versionen: tre fraser + hand på bröstet (2–3 min).",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110216",
    slug: "sleep-story-brain-dump",
    category: "recovery",
    difficulty: 2,
    durationMinutes: 10,
    minDurationMinutes: 5,
    premium: true,
    title: "Hjärntömning före sömn",
    summary: "Töm snurrande uppgifter och tankar på papper före sängen (Plus: längre, fasad guide).",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Skriv uppgifter och tankar 5–8 minuter. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Stjärnmarkera bara det som spelar roll imorgon.", "Fas 3 · Kärna B (2 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Stäng blocket och börja nedvarvning.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Skriv tre öppna loopar och stäng sidan.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110217",
    slug: "creative-twenty",
    category: "presence",
    difficulty: 3,
    durationMinutes: 20,
    minDurationMinutes: 10,
    premium: true,
    title: "Tjugo kreativa minuter",
    summary: "Skapa något “onyttigt” med glädje i tjugo minuter (Plus: längre, fasad guide).",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Välj medium (skiss, lera, collage, fria noter, musik). Andas ut en extra gång.", "Fas 2 · Kärna A (4 min): Sätt 20 minuter; inget resultatmål.", "Fas 3 · Kärna B (5 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (4 min): Sluta när timern ringer; behåll eller släng fritt.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Fem minuters klotter.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110218",
    slug: "forgiveness-light",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Lätt förlåtelseövning",
    summary: "Lossa greppet om ett litet agg utan att skriva om historien (Plus: längre, fasad guide).",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Namnge en liten skada (inte övergrepp). Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Skriv vad du behövde.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Skriv en mening som lättar greppet utan att ursäkta skadan.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Skriv: “Jag släpper att bära detta idag.”",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110219",
    slug: "social-meal-or-tea",
    category: "connection",
    difficulty: 2,
    durationMinutes: 20,
    minDurationMinutes: 10,
    premium: true,
    title: "Gemensam måltid eller te",
    summary: "Dela mat eller dryck med någon, telefoner ner (Plus: längre, fasad guide).",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Bjud in någon eller delta i en hushållsmåltid. Andas ut en extra gång.", "Fas 2 · Kärna A (4 min): Håll telefoner borta om ni är överens.", "Fas 3 · Kärna B (5 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (4 min): Ställ en riktig fråga och lyssna färdigt.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Drick te i ett samtal, kamera valfritt.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110220",
    slug: "mobility-flow-twelve",
    category: "movement",
    difficulty: 2,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Tolv minuters mobilitetsflöde",
    summary: "Ett mjukt helkroppsmobilitetsflöde (Plus: längre, fasad guide).",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Flöda genom anklar, höfter, rygg, axlar ungefär 12 minuter. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Långsamt tempo; andas hela tiden.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Avsluta i en ställning som känns öppen.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Fyra minuter: bara höfter och bröstrygg.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110221",
    slug: "worry-time-appointment",
    category: "recovery",
    difficulty: 3,
    durationMinutes: 15,
    minDurationMinutes: 8,
    premium: true,
    title: "Bokad orostid",
    summary: "CBT: samla oro under dagen, möt den i en tidsatt stund — inte hela tiden.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa. Orostid är en beprövad teknik mot ältande när oro tar hela dagen.",
    instructions: ["Fas 1 · Boka (1 min): välj 15 minuter i dag (inte i sängen). Skriv tid och plats.", "Fas 2 · Under dagen: när oro dyker upp, skriv en rad i en lista och säg Den får vänta till [tid].", "Fas 3 · Orostid start (2 min): öppna listan. Andas. Du får oroa dig — men bara här.", "Fas 4 · Arbete (10 min): gå igenom punkter. Lös det som går på 2 min; parkera resten till i morgon.", "Fas 5 · Stäng (2 min): stäng listan fysiskt. En handling som markerar slut (resa dig, tvätta händer)."],
    smallerVersion: "Skriv oro i 2 min + boka 10 min senare i dag.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110222",
    slug: "photo-walk-presence",
    category: "gratitude",
    difficulty: 2,
    durationMinutes: 15,
    minDurationMinutes: 7,
    premium: true,
    title: "Fotopromenad för närvaro",
    summary: "Gå och ta fem foton av vardags-skönhet (Plus: längre, fasad guide).",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Gå 15 minuter. Andas ut en extra gång.", "Fas 2 · Kärna A (3 min): Ta exakt fem foton av detaljer du brukar missa.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (3 min): Titta igenom dem en gång med uppskattning.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Tre foton hemma.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110223",
    slug: "assertive-request",
    category: "connection",
    difficulty: 3,
    durationMinutes: 10,
    minDurationMinutes: 5,
    premium: true,
    title: "En tydlig begäran",
    summary: "Be om något du behöver, tydligt och vänligt (Plus: längre, fasad guide).",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Skriv en tydlig begäran (vad, när, kort varför). Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Lämna den till rätt person.", "Fas 3 · Kärna B (2 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Acceptera ja, nej eller förhandling utan att kollapsa.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Öva meningen högt först.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110224",
    slug: "energy-audit",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 12,
    minDurationMinutes: 6,
    premium: true,
    title: "Energi-audit",
    summary: "Kartlägg vad som ger och dränerar energi den här veckan (Plus: längre, fasad guide).",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Lista energigivare och energidränerare. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Markera vad du kan justera 10%.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Välj en gräns eller återhämtningshandling.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Ringa in en dränerare att krympa imorgon.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110225",
    slug: "yoga-style-soft-flow",
    category: "movement",
    difficulty: 2,
    durationMinutes: 15,
    minDurationMinutes: 7,
    premium: true,
    title: "Mjuk yoga-liknande flöde",
    summary: "Ett mjukt flöde för andning och mobilitet (Plus: längre, fasad guide).",
    rationale: "Lätt rörelse kan ge snabb humör- och energiförändring utan att det blir “träning”. Akut effekt av promenad/mjuk rörlighet är välbelagd; håll det snällt mot kroppen. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Rör dig mjukt 15 minuter (katt-ko, foldningar, lätta vridningar). Andas ut en extra gång.", "Fas 2 · Kärna A (3 min): Koppla andning till rörelse.", "Fas 3 · Kärna B (3 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (3 min): Vila i en bekväm position en minut.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Fem minuter katt-ko och vilande barnposition-liknande vila.",
    reflectionQuestions: ["Hjälpte den mindre versionen?", "Vad ändrades de första 30 sekunderna efter?", "Vem eller vad stöttade dig?", "Hoppa över, behåll eller anpassa?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110226",
    slug: "future-self-letter",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 14,
    minDurationMinutes: 7,
    premium: true,
    title: "Brev till framtida dig",
    summary: "Skriv till dig om 30 dagar: omtanke, realism, ett hopp — inte press.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Fas 1 · Välj tidshorisont (1 min): t.ex. om 30 dagar. Rubrik: Till mig [datum].", "Fas 2 · Nu (3 min): hur är det just nu, ärligt, utan snygga till.", "Fas 3 · Omtanke (4 min): vad du hoppas att framtida dig minns att du klarade / bar.", "Fas 4 · Råd (4 min): tre snälla, realistiska råd — inga ultimata krav.", "Fas 5 · Försegling (2 min): en mening att återkomma till. Spara brevet."],
    smallerVersion: "Ett stycke till dig om en vecka.",
    reflectionQuestions: ["Ärligt: började du?", "Energi efteråt, ungefär 0-10?", "Vad skulle du tweaka?", "Rekommendera till en vän?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110227",
    slug: "gratitude-plus-awe",
    category: "gratitude",
    difficulty: 2,
    durationMinutes: 10,
    minDurationMinutes: 5,
    premium: true,
    title: "Tacksamhet plus förundran",
    summary: "Kombinera tack med ett ögonblick av rymd eller undran (Plus: längre, fasad guide).",
    rationale: "Tacksamhet funkar bäst när den är konkret och nylig — en sak, inte “allt”. Studier visar oftast humörlyft när man specificerar, inte när man tvingar positivitet. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Namnge tre tacksamheter. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Sök en awe-signal (himmel, musik, vetenskapsklipp, stort träd).", "Fas 3 · Kärna B (2 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Stanna med den två minuter.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "En tacksamhet och 30 sekunder himmel.",
    reflectionQuestions: ["Klart, delvis eller skippat?", "Mjukare eller mer vaken?", "Vad lärde du dig om dig själv?", "Boka en till?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110228",
    slug: "coach-yourself-kindly",
    category: "recovery",
    difficulty: 2,
    durationMinutes: 10,
    minDurationMinutes: 5,
    premium: true,
    title: "Coach dig själv snällt",
    summary: "Prata dig igenom en stressor som en bra coach skulle (Plus: längre, fasad guide).",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Namnge stressorn. Andas ut en extra gång.", "Fas 2 · Kärna A (2 min): Skriv vad en bra coach skulle säga (fakta, alternativ, nästa steg).", "Fas 3 · Kärna B (2 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (2 min): Ta det minsta nästa steget om energin räcker.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "En coachmening och ett nästa steg.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110229",
    slug: "deep-friend-hour",
    category: "connection",
    difficulty: 3,
    durationMinutes: 20,
    minDurationMinutes: 10,
    premium: true,
    title: "Djup vän-tid",
    summary: "Skydda ungefär 20 minuter för ostörd kontakt (Plus: längre, fasad guide).",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”. Plus-versionen är en guidad ritual i flera faser — avsiktligt mer än free.",
    instructions: ["Fas 1 · Landning (2 min): Boka med någon du litar på. Andas ut en extra gång.", "Fas 2 · Kärna A (4 min): Kom överens om telefoner borta.", "Fas 3 · Kärna B (5 min): Fördjupa samma spår. Om du tappar fokus — återvänd utan självkritik.", "Fas 4 · Integration (4 min): Använd prompts: högt, lågt, behov.", "Fas 5 · Avslut (2 min): Tre andetag. En mening om vad du tar med dig. Klart."],
    smallerVersion: "Tio ostörda minuter räknas.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110230",
    slug: "recovery-stack-evening",
    category: "recovery",
    difficulty: 3,
    durationMinutes: 18,
    minDurationMinutes: 8,
    premium: true,
    title: "Kvällens recovery-stack",
    summary: "Staplade mikrofärdigheter: ljus, andning, dump, stretch, vänlig avslutning.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa. Stacking av korta färdigheter ger ofta mer än en “perfekt” teknik.",
    instructions: ["Fas 1 · Ljus & skärm (3 min): dämpa, telefon undan eller flygplansläge.", "Fas 2 · Andning (4 min): in 4 ut 6, eller box om du föredrar struktur.", "Fas 3 · Brain dump (4 min): öppna loopar på papper. Stjärna max en sak för i morgon.", "Fas 4 · Mjuk stretch (5 min): nacke, axlar, höfter — ingen yoga-prestation.", "Fas 5 · Avslut (2 min): hand på bröstet + en vänlig mening. Klart för natten."],
    smallerVersion: "Andning 2 min + dump 2 rader + sänk axlarna.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110401",
    slug: "self-compassion-break-short",
    category: "presence",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Självmedkänsla-paus",
    summary: "Tre steg: se att det är svårt, kom ihåg att det är mänskligt, möt dig snällt.",
    rationale: "Självmedkänsla-pausen (Neff) kombinerar mindfulness, common humanity och self-kindness. Korta övningar kan mjukna självkritik; det är stöd i vardagen, inte behandling.",
    instructions: ["Lägg märke till det som stramar eller gör ont just nu. Säg tyst: Det här är svårt.", "Lägg till: Andra känner så här också. Det hör till att vara människa.", "Hand på bröst eller kind. En vänlig fras, t.ex. Må jag vara snäll mot mig i det här."],
    smallerVersion: "Hand på bröstet + en vänlig mening.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110402",
    slug: "friend-to-self",
    category: "meaning",
    difficulty: 1,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "Som till en vän",
    summary: "Skriv hur du skulle stötta en vän — ge dig själv samma ord.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Välj något du nyligen dömt dig för. Skriv det i en rad.", "Skriv 2–3 meningar du skulle säga till en god vän i samma situation.", "Läs dem till dig själv en gång. Låt dem gälla dig — även om det känns ovant."],
    smallerVersion: "En vänlig mening du skulle ge en vän — säg den till dig.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110403",
    slug: "soothing-touch",
    category: "recovery",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Mjuk beröring",
    summary: "Soothing touch: hand på kind, bröst eller mage i två minuter.",
    rationale: "Kort återhämtning handlar ofta om att dämpa påslag: andning, mjuk avspänning eller en tydlig paus. Det är inte terapi — det är att ge nervsystemet en tydligare signal att det får landa.",
    instructions: ["Välj plats: kind, bröst eller mage. Tryck mjukt.", "Andas i ca 2 minuter. Om det känns obehagligt: byt plats eller sluta.", "Avsluta med en önskan: Må jag ge mig själv lite mer tålamod i dag."],
    smallerVersion: "Hand på kinden, fem andetag.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110404",
    slug: "common-humanity-note",
    category: "connection",
    difficulty: 1,
    durationMinutes: 3,
    minDurationMinutes: 1,
    premium: false,
    title: "Du är inte ensam i det här",
    summary: "Namnge kampen och påminn dig om att det är mänskligt — common humanity.",
    rationale: "Social kontakt är en av de starkaste hälsofaktorerna. Korta, konkreta signaler (meddelande, tack, närvaro) slår ofta vaga ambitioner om att “träffas mer”.",
    instructions: ["Skriv: Just nu kämpar jag med … (en mening).", "Lägg till: Andra har känt liknande. Det är mänskligt.", "Om du vill: tänk på en person som skulle förstå — utan att behöva kontakta dem."],
    smallerVersion: "Tyst: Det här är mänskligt. Jag är inte ensam.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110405",
    slug: "soften-inner-critic",
    category: "presence",
    difficulty: 2,
    durationMinutes: 4,
    minDurationMinutes: 2,
    premium: false,
    title: "Mjukna den inre kritikern",
    summary: "Hör den hårda rösten, namnge den, svara mer rättvist.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Skriv den kritiska tanken ordagrant (en rad).", "Ge rösten ett tillfälligt namn (t.ex. Domaren). Du är inte den rösten.", "Skriv ett kort svar som en snäll coach: fakta + förståelse + minsta nästa steg."],
    smallerVersion: "Namnge kritiken + en mildare mening till dig själv.",
    reflectionQuestions: ["Vad gjorde du konkret?", "Någon förskjutning i humör eller kropp?", "Vad gjorde det lätt eller svårt?", "Vill du ha detta i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110406",
    slug: "self-kindness-mantra",
    category: "meaning",
    difficulty: 1,
    durationMinutes: 2,
    minDurationMinutes: 1,
    premium: false,
    title: "Tre vänliga meningar",
    summary: "Välj tre korta fraser du kan återvända till när du är hård mot dig själv.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen.",
    instructions: ["Välj tre meningar, t.ex. Jag gör så gott jag kan. Det är okej att vila. Jag förtjänar samma omtanke som andra.", "Säg dem tyst tre gånger, långsamt.", "Spara dem i anteckningar om du vill kunna hitta dem igen. Lägg märke till en sak i kroppen efteråt."],
    smallerVersion: "En vänlig mening, tre gånger.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110407",
    slug: "self-compassion-journal",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 14,
    minDurationMinutes: 7,
    premium: true,
    title: "Självmedkänsla-journal",
    summary: "Skriv genom tre linser: vad hände, det är mänskligt, hur kan jag stötta mig.",
    rationale: "Mening byggs av små handlingar i linje med vad som är viktigt för dig (värden), inte av stora livsbeslut varje dag. Ett litet steg räcker för att testa riktningen. Journaling med self-compassion-struktur ger mer djup än en snabb fras.",
    instructions: ["Fas 1 · Välj händelse (2 min): något i dag som gav självkritik eller tyngd. En rubrik räcker.", "Fas 2 · Mindfulness (3 min): skriv hur det kändes i kroppen och vilka tankar som kom — utan att debattera dig själv.", "Fas 3 · Common humanity (3 min): skriv hur det är mänskligt / delat. Vad skulle du säga till en vän?", "Fas 4 · Self-kindness (4 min): 5–8 rader stöd till dig. Fakta, värme, rimligt nästa steg.", "Fas 5 · Stäng (2 min): stryk under en mening att minnas. Stäng boken. Klart."],
    smallerVersion: "Tre korta stycken: händelse · mänskligt · en snäll mening.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110408",
    slug: "compassionate-body-scan",
    category: "recovery",
    difficulty: 3,
    durationMinutes: 15,
    minDurationMinutes: 6,
    premium: true,
    title: "Medkännande kroppsscan",
    summary: "En guidad 15-minuters scan: fötter → huvud, med vänlighet till spänning — inte krav att slappna av.",
    rationale: "Plus-rituell: body awareness + self-kindness. Spänning möts med värme i stället för press. Längre än free-stegen, med tydliga faser.",
    instructions: ["Fas 1 · Förberedelse (2 min): sitt eller ligg. Stäng ögonen om det känns okej. Tre långsamma andetag. Säg tyst: Jag behöver inte prestera nu.", "Fas 2 · Nedre kropp (4 min): uppmärksamhet i fötter, vader, knän, lår. Vid spänning: stanna 15–20 sek, mjukna lite, andas ut. Tyst: Må den här delen få vila.", "Fas 3 · Bål & armar (4 min): mage, bröst, rygg, händer, armar, axlar. Samma mönster. Om tankar kommer: notera och återvänd till kroppen.", "Fas 4 · Huvud & ansikte (3 min): nacke, käke, panna, ögon. Låt ansiktet bli tyngre. En hand på bröstet eller kinden.", "Fas 5 · Avslut (2 min): tre vänliga andetag. En mening till dig själv (t.ex. Det räcker så här). Öppna ögonen sakta."],
    smallerVersion: "Sex minuter: fötter–mage–axlar + hand på bröstet och en vänlig mening.",
    reflectionQuestions: ["Vilken fas kändes mest möjlig?", "Var i kroppen var det mest spänt — och vad hände när du mötte det mjukt?", "Kändes det som krav eller som vila?", "Vill du ha den här ritualen i verktygslådan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110409",
    slug: "letter-from-compassion",
    category: "meaning",
    difficulty: 3,
    durationMinutes: 15,
    minDurationMinutes: 8,
    premium: true,
    title: "Brev från medkänsla",
    summary: "Skriv som om en vis, snäll vän skriver till dig om något svårt.",
    rationale: "Compassionate letter writing ger tid att formulera stöd. Längre än free “som till en vän”-övningen.",
    instructions: ["Fas 1 · Ämne (2 min): en sak du skäms över eller dömer dig för. En rubrik.", "Fas 2 · Rösten (2 min): tänk dig en vis, snäll vän (eller version av dig).", "Fas 3 · Skriv (8 min): 10–15 minuters mål — men 8 räcker. Förståelse, perspektiv, hopp utan press.", "Fas 4 · Läs (2 min): sakta. Markera en mening.", "Fas 5 · Förvara (1 min): lägg undan. Du behöver inte dela det."],
    smallerVersion: "Ett stycke (5 min) från en snäll vän till dig.",
    reflectionQuestions: ["Hur långt kom du?", "Stress upp, ner eller samma?", "Ett ord för upplevelsen?", "Igen den här veckan?"],
  },
  {
    id: "11111111-1111-4111-8111-111111110410",
    slug: "compassionate-friend-visualize",
    category: "presence",
    difficulty: 2,
    durationMinutes: 12,
    minDurationMinutes: 5,
    premium: true,
    title: "Den medkännande vännen",
    summary: "Föreställ dig en vänlig närvaro som ser dig med värme — MSC-inspirerat.",
    rationale: "Närvaro-övningar tränar uppmärksamhet mot sinnesintryck i stället för oro-loopar. Forskning om mindfulness och uppmärksamhetsvila visar oftast små men märkbara skiften — prova och känn efter.",
    instructions: ["Fas 1 · Stillhet (2 min): blunda om okej. Andas. Ryggen stödd.", "Fas 2 · Bild (3 min): en person, djur eller ljus som är helt på din sida.", "Fas 3 · Mottagande (4 min): vad säger de? Vilken blick? Ta emot utan att argumentera.", "Fas 4 · Om det är svårt (2 min): stanna bara i känslan av att någon bryr sig — ord krävs inte.", "Fas 5 · Ankare (1 min): skriv en mening du tar med dig."],
    smallerVersion: "Två minuter: en vänlig blick och en mening.",
    reflectionQuestions: ["Gjorde du klart det?", "Hur känns det nu?", "Vad stack ut?", "Vill du prova liknande igen?"],
  },
];

export function getExperimentById(id: string): Experiment | undefined {
  return MOCK_EXPERIMENTS.find((e) => e.id === id);
}

export function getExperimentBySlug(slug: string): Experiment | undefined {
  return MOCK_EXPERIMENTS.find((e) => e.slug === slug);
}
